<template>
  <ErrorDisplay v-if="error" :message="error.message" :loading="loading" @retry="loadData" />
  <v-select
    v-else
    :variant="!showLabel ? 'solo' : undefined"
    :density="!showLabel ? 'compact' : 'default'"
    :label="showLabel ? 'Lagerhalle' : ''"
    hide-details
    class="mt-3 mb-2"
    v-model="internalValue"
    item-title="Bezeichnung"
    item-value="Lagerhalle"
    :items="loading ? [] : data"
    :loading="loading"
    placeholder="--- Lagerhalle wählen ---"
    return-object
  />
</template>

<script setup lang="ts">
import ErrorDisplay from './ErrorDisplay.vue';
import {getWarehouses} from '@/api/module/warehouse';
import Warehouse from '@/model/Warehouse';
import pinia from '@/plugins/pinia';
import {useSettings} from '@/store/settings';
import {watch} from 'vue';
import {computed, ref} from 'vue';
import {PropType} from 'vue';

const emits = defineEmits({
  'update:modelValue'(val: Warehouse | null) {
    return val === null || val instanceof Warehouse;
  },
});

const props = defineProps({
  showLabel: {
    type: Boolean,
    default: false,
  },
  useModel: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [Object] as PropType<Warehouse | null>,
    default: useSettings(pinia).selectedWarehouse,
  },
  client: {
    type: Number,
    required: false,
  },
  customerId: {
    type: String,
    default: null,
  },
  subsidiary: {
    type: Number,
    required: false,
  },
});

const settingsStore = useSettings();

const internalValue = computed({
  get() {
    if (props.useModel) return props.modelValue;

    return settingsStore.selectedWarehouse;
  },
  set(newVal: Warehouse | null) {
    if (props.useModel) {
      emits('update:modelValue', newVal);
      return;
    }

    settingsStore.selectedWarehouse = newVal;
  },
});

const loading = ref(true);
const data = ref<Warehouse[]>([]);
const error = ref<Error | null>(null);

function loadData() {
  loading.value = true;
  const settings = useSettings();
  const Niederlassung = props.subsidiary ?? settings.selectedSubsidiary;
  const client = props.client ?? settings.client;
  if (Niederlassung === null || client === null) {
    data.value = [];
    loading.value = false;
    error.value = null;
    return;
  }
  getWarehouses({Niederlassung: props.subsidiary ?? useSettings().selectedSubsidiary ?? undefined}, [], null, 0, props.client, props.customerId)
      .then(([d]) => {
        data.value = d;
        error.value = null;
      })
      .catch((e) => {
        error.value = e;
        data.value = [];
      })
      .finally(() => loading.value = false);
}

loadData(); // onCreated()

watch(() => props.client, () => loadData());
watch(() => props.subsidiary, () => loadData());

defineExpose({
  internalValue,
  emits,
  loading,
  data,
  error,
  ErrorDisplay,
});
</script>
